import type {
  ApplicationAttemptBinding,
  ApplicationKgPreflightBinding,
} from "@cogo/acceptance-contracts";
import type { ContentHash, ContentRef } from "@cogo/publisher-core";

import {
  applicationProfileContentRef,
  assertApplicationProfileIntrinsicIntegrity,
  type ApplicationProfile,
} from "./application-profile";
import {
  assertCompiledApplicationOntologyIntrinsicIntegrity,
  compiledApplicationOntologyContentRef,
  type CompiledApplicationOntology,
} from "./compiled-application-ontology";
import {
  assertRequiredApplicationTopologySliceIntrinsicIntegrity,
  requiredApplicationTopologySliceContentRef,
  type RequiredApplicationTopologySlice,
} from "./required-application-topology-slice";
import {
  assertRequiredCapabilitySliceIntrinsicIntegrity,
  requiredCapabilitySliceContentRef,
  type RequiredCapabilitySlice,
} from "./required-capability-slice";

export interface ApplicationOntologyGenerationBinding {
  schemaVersion: "cogo.application-ontology-generation-binding.v1";
  profileRef: ContentRef;
  applicationOntologyPackageRef: ContentRef;
  profileSelectionRef: ContentRef;
  projectScopeRef: ContentRef;
  actorScopeRef: ContentRef;
  manifestRef: ContentRef;
  compiledOntologyRef: ContentRef;
  requiredCapabilitySliceRef: ContentRef;
  requiredApplicationTopologySliceRef: ContentRef;
  requiredCapabilityCatalogRef: ContentRef;
  runtimeConsumerCatalogRef: ContentRef;
  applicationGenerationEligibilityRef: ContentRef;
  hostManifestRef: ContentRef;
  validatorRef: ContentRef;
  compilerIdentityRef: ContentRef;
  promptTemplateRef: ContentRef;
  sealedInputRef: ContentRef;
  kgPreflight: ApplicationKgPreflightBinding;
  modelPolicyRef: ContentRef;
  evidenceRealmRef: ContentRef;
  receiptChainHeadRef: ContentRef;
}

export type ApplicationEvidenceRole =
  | "profile"
  | "application-ontology-package"
  | "profile-selection"
  | "project-scope"
  | "actor-scope"
  | "acceptance-manifest"
  | "compiled-application-ontology"
  | "required-capability-slice"
  | "required-application-topology-slice"
  | "required-capability-catalog"
  | "runtime-consumer-catalog"
  | "application-generation-eligibility"
  | "host-capability-manifest"
  | "validator"
  | "compiler-identity"
  | "prompt-template"
  | "sealed-input"
  | "kg-observation"
  | "kg-producer-attestation"
  | "kg-snapshot"
  | "kg-readback"
  | "evidence-realm"
  | "receipt-chain-head"
  | "generation-evidence-envelope"
  | "application-package"
  | "generated-artifact"
  | "artifact-validation-observation"
  | "artifact-persistence-eligibility"
  | "source-tree"
  | "runtime"
  | "persistence-write"
  | "fresh-readback"
  | "readback-artifact"
  | "model-policy"
  | "model-invocation";

export interface ApplicationEvidenceRoleResolver {
  roleOf(ref: ContentRef): ApplicationEvidenceRole;
}

const HASH = /^sha256:[a-f0-9]{64}$/;
const BINDING_KEYS = [
  "schemaVersion", "profileRef", "applicationOntologyPackageRef",
  "profileSelectionRef", "projectScopeRef", "actorScopeRef", "manifestRef",
  "compiledOntologyRef", "requiredCapabilitySliceRef",
  "requiredApplicationTopologySliceRef", "requiredCapabilityCatalogRef",
  "runtimeConsumerCatalogRef", "applicationGenerationEligibilityRef",
  "hostManifestRef", "validatorRef", "compilerIdentityRef",
  "promptTemplateRef", "sealedInputRef", "kgPreflight", "modelPolicyRef",
  "evidenceRealmRef", "receiptChainHeadRef",
] as const;
const KG_KEYS = [
  "observationRef", "observationHash", "producerAttestationRef", "snapshotRef",
  "snapshotHash", "readbackRef", "readbackHash", "boundedQueryHash",
] as const;
const ATTEMPT_KEYS = [
  "attemptId", "seriesId", "seriesHash", "sealedInputHash",
  "profileSelectionRef", "projectScopeRef", "actorScopeRef",
  "evidenceRealmHash", "attemptOrdinal", "attemptHash", "kgPreflight",
] as const;

function plain(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype &&
    Object.getOwnPropertySymbols(value).length === 0;
}

export function assertPlainJsonContract(value: unknown, label: string): void {
  const ancestors = new WeakSet<object>();
  const fail = (): never => {
    throw new Error(`${label}-non-plain-json`);
  };
  const visit = (entry: unknown): void => {
    if (entry === null || typeof entry === "string" || typeof entry === "boolean") return;
    if (typeof entry === "number") {
      if (!Number.isFinite(entry)) fail();
      return;
    }
    if (typeof entry !== "object" || entry === null) return fail();
    const object = entry as object;
    if (ancestors.has(object)) fail();
    const isArray = Array.isArray(entry);
    if (Object.getPrototypeOf(entry) !== (isArray ? Array.prototype : Object.prototype)) fail();
    ancestors.add(object);
    try {
      const keys = Reflect.ownKeys(object);
      if (keys.some((key) => typeof key === "symbol")) fail();
      if (isArray) {
        const array = entry as unknown[];
        const stringKeys = keys as string[];
        if (!stringKeys.includes("length")) fail();
        for (let index = 0; index < array.length; index += 1) {
          if (!Object.prototype.hasOwnProperty.call(array, String(index))) fail();
        }
        for (const key of stringKeys) {
          const descriptor = Object.getOwnPropertyDescriptor(array, key);
          if (!descriptor) return fail();
          if (key === "length") {
            if (descriptor.get || descriptor.set || descriptor.enumerable) fail();
            continue;
          }
          if (!/^(0|[1-9][0-9]*)$/.test(key) || Number(key) >= array.length ||
            !descriptor.enumerable || descriptor.get || descriptor.set ||
            !("value" in descriptor)) return fail();
          visit(descriptor.value);
        }
      } else {
        for (const key of keys as string[]) {
          const descriptor = Object.getOwnPropertyDescriptor(object, key);
          if (!descriptor || !descriptor.enumerable || descriptor.get || descriptor.set ||
            !("value" in descriptor)) return fail();
          visit(descriptor.value);
        }
      }
    } finally {
      ancestors.delete(object);
    }
  };
  visit(value);
}

function exactKeys(value: unknown, expected: readonly string[]): value is Record<string, unknown> {
  if (!plain(value)) return false;
  const actual = Object.keys(value);
  return actual.length === expected.length &&
    actual.every((key) => expected.includes(key)) &&
    expected.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function contentHash(value: unknown): value is ContentHash {
  return typeof value === "string" && HASH.test(value);
}

function contentRef(value: unknown): value is ContentRef {
  return exactKeys(value, ["ref", "contentHash"]) &&
    typeof value.ref === "string" && value.ref.trim().length > 0 &&
    contentHash(value.contentHash);
}

function sameRef(left: ContentRef, right: ContentRef): boolean {
  return left.ref === right.ref && left.contentHash === right.contentHash;
}

function sameRefSet(left: readonly ContentRef[], right: readonly ContentRef[]): boolean {
  if (left.length !== right.length) return false;
  const sort = (values: readonly ContentRef[]) => [...values].sort((a, b) =>
    a.ref.localeCompare(b.ref) || a.contentHash.localeCompare(b.contentHash));
  const sortedLeft = sort(left);
  const sortedRight = sort(right);
  return sortedLeft.every((reference, index) => sameRef(reference, sortedRight[index]!));
}

function cloneRef(value: ContentRef): ContentRef {
  return { ref: value.ref, contentHash: value.contentHash };
}

function cloneKgPreflight(value: ApplicationKgPreflightBinding): ApplicationKgPreflightBinding {
  return {
    observationRef: cloneRef(value.observationRef),
    observationHash: value.observationHash,
    producerAttestationRef: cloneRef(value.producerAttestationRef),
    snapshotRef: cloneRef(value.snapshotRef),
    snapshotHash: value.snapshotHash,
    readbackRef: cloneRef(value.readbackRef),
    readbackHash: value.readbackHash,
    boundedQueryHash: value.boundedQueryHash,
  };
}

function assertDistinctRefs(refs: readonly ContentRef[]): void {
  const seen = new Set<string>();
  for (const reference of refs) {
    if (seen.has(reference.ref)) throw new Error("duplicate-evidence-role-ref");
    seen.add(reference.ref);
  }
}

function validKgPreflight(
  value: unknown,
  requireHashCoherence = true,
): value is ApplicationKgPreflightBinding {
  if (!exactKeys(value, KG_KEYS)) return false;
  for (const key of [
    "observationRef", "producerAttestationRef", "snapshotRef", "readbackRef",
  ] as const) if (!contentRef(value[key])) return false;
  for (const key of [
    "observationHash", "snapshotHash", "readbackHash", "boundedQueryHash",
  ] as const) if (!contentHash(value[key])) return false;
  const kg = value as unknown as ApplicationKgPreflightBinding;
  return !requireHashCoherence || (kg.observationRef.contentHash === kg.observationHash &&
    kg.snapshotRef.contentHash === kg.snapshotHash &&
    kg.readbackRef.contentHash === kg.readbackHash);
}

function validAttempt(value: unknown): value is ApplicationAttemptBinding {
  if (!exactKeys(value, ATTEMPT_KEYS) ||
    typeof value.attemptId !== "string" || value.attemptId.trim().length === 0 ||
    typeof value.seriesId !== "string" || value.seriesId.trim().length === 0 ||
    !contentHash(value.seriesHash) || !contentHash(value.sealedInputHash) ||
    !contentRef(value.profileSelectionRef) || !contentRef(value.projectScopeRef) ||
    !contentRef(value.actorScopeRef) || !contentHash(value.evidenceRealmHash) ||
    !Number.isInteger(value.attemptOrdinal) || (value.attemptOrdinal as number) < 1 ||
    !contentHash(value.attemptHash) || !validKgPreflight(value.kgPreflight)) {
    return false;
  }
  return true;
}

function parseApplicationOntologyGenerationBinding(
  value: unknown,
  requireDistinctRefs: boolean,
  requireKgHashCoherence = true,
): ApplicationOntologyGenerationBinding {
  assertPlainJsonContract(value, "application-ontology-generation-binding");
  if (!exactKeys(value, BINDING_KEYS) ||
    value.schemaVersion !== "cogo.application-ontology-generation-binding.v1") {
    throw new Error("application-ontology-generation-binding-invalid");
  }
  const refs: ContentRef[] = [];
  for (const key of BINDING_KEYS.filter((key) => key.endsWith("Ref"))) {
    const reference = value[key];
    if (!contentRef(reference)) {
      throw new Error("application-ontology-generation-binding-invalid");
    }
    refs.push(reference);
  }
  if (!validKgPreflight(value.kgPreflight, requireKgHashCoherence)) {
    throw new Error("application-ontology-generation-binding-invalid");
  }
  refs.push(
    value.kgPreflight.observationRef,
    value.kgPreflight.producerAttestationRef,
    value.kgPreflight.snapshotRef,
    value.kgPreflight.readbackRef,
  );
  if (requireDistinctRefs) {
    try {
      assertDistinctRefs(refs);
    } catch {
      throw new Error("application-ontology-generation-binding-invalid");
    }
  }
  return value as unknown as ApplicationOntologyGenerationBinding;
}

export function assertApplicationOntologyGenerationBinding(
  value: unknown,
): ApplicationOntologyGenerationBinding {
  return parseApplicationOntologyGenerationBinding(value, true);
}

export function assertApplicationEvidenceRole(
  resolver: ApplicationEvidenceRoleResolver,
  field: string,
  referenceValue: ContentRef,
  expected: ApplicationEvidenceRole,
): void {
  try {
    if (resolver.roleOf(referenceValue) === expected) return;
  } catch {
    // Normalize resolver failures to the field-qualified contract error.
  }
  throw new Error(`application-evidence-role-mismatch:${field}`);
}

export function assertApplicationOntologyGenerationBindingRoleIntegrity(
  value: unknown,
  resolver: ApplicationEvidenceRoleResolver,
  prefix = "binding",
): ApplicationOntologyGenerationBinding {
  const binding = parseApplicationOntologyGenerationBinding(value, false, false);
  const roles = {
    profileRef: "profile",
    applicationOntologyPackageRef: "application-ontology-package",
    profileSelectionRef: "profile-selection",
    projectScopeRef: "project-scope",
    actorScopeRef: "actor-scope",
    manifestRef: "acceptance-manifest",
    compiledOntologyRef: "compiled-application-ontology",
    requiredCapabilitySliceRef: "required-capability-slice",
    requiredApplicationTopologySliceRef: "required-application-topology-slice",
    requiredCapabilityCatalogRef: "required-capability-catalog",
    runtimeConsumerCatalogRef: "runtime-consumer-catalog",
    applicationGenerationEligibilityRef: "application-generation-eligibility",
    hostManifestRef: "host-capability-manifest",
    validatorRef: "validator",
    compilerIdentityRef: "compiler-identity",
    promptTemplateRef: "prompt-template",
    sealedInputRef: "sealed-input",
    modelPolicyRef: "model-policy",
    evidenceRealmRef: "evidence-realm",
    receiptChainHeadRef: "receipt-chain-head",
  } as const satisfies Record<string, ApplicationEvidenceRole>;
  for (const [field, expectedRole] of Object.entries(roles) as [
    keyof typeof roles,
    ApplicationEvidenceRole,
  ][]) {
    assertApplicationEvidenceRole(
      resolver,
      `${prefix}.${field}`,
      binding[field],
      expectedRole,
    );
  }
  const kgRoles = {
    observationRef: "kg-observation",
    producerAttestationRef: "kg-producer-attestation",
    snapshotRef: "kg-snapshot",
    readbackRef: "kg-readback",
  } as const satisfies Record<string, ApplicationEvidenceRole>;
  for (const [field, expectedRole] of Object.entries(kgRoles) as [
    keyof typeof kgRoles,
    ApplicationEvidenceRole,
  ][]) {
    assertApplicationEvidenceRole(
      resolver,
      `${prefix}.kgPreflight.${field}`,
      binding.kgPreflight[field],
      expectedRole,
    );
  }
  return parseApplicationOntologyGenerationBinding(binding, true, true);
}

export function createApplicationOntologyGenerationBinding(input: {
  profile: ApplicationProfile;
  compiledOntology: CompiledApplicationOntology;
  requiredCapabilitySlice: RequiredCapabilitySlice;
  requiredApplicationTopologySlice: RequiredApplicationTopologySlice;
  attempt: ApplicationAttemptBinding;
  profileSelectionRef: ContentRef;
  projectScopeRef: ContentRef;
  actorScopeRef: ContentRef;
  applicationGenerationEligibilityRef: ContentRef;
  validatorRef: ContentRef;
  compilerIdentityRef: ContentRef;
  promptTemplateRef: ContentRef;
  sealedInputRef: ContentRef;
  modelPolicyRef: ContentRef;
  evidenceRealmRef: ContentRef;
  receiptChainHeadRef: ContentRef;
}): ApplicationOntologyGenerationBinding {
  try {
    assertPlainJsonContract(input, "application-ontology-generation-binding-input");
    assertApplicationProfileIntrinsicIntegrity(input.profile);
    assertCompiledApplicationOntologyIntrinsicIntegrity(input.compiledOntology);
    assertRequiredCapabilitySliceIntrinsicIntegrity(input.requiredCapabilitySlice);
    assertRequiredApplicationTopologySliceIntrinsicIntegrity(
      input.requiredApplicationTopologySlice,
    );
    if (!validAttempt(input.attempt)) throw new Error("identity");

    const profileRef = applicationProfileContentRef(input.profile);
    const compiledOntologyRef = compiledApplicationOntologyContentRef(
      input.compiledOntology,
    );
    const requiredCapabilitySliceRef = requiredCapabilitySliceContentRef(
      input.requiredCapabilitySlice,
    );
    const requiredApplicationTopologySliceRef =
      requiredApplicationTopologySliceContentRef(
        input.requiredApplicationTopologySlice,
      );
    const profileBody = input.profile.body;
    const compiledBody = input.compiledOntology.body;

    const internalPairs: readonly [ContentRef, ContentRef][] = [
      [profileRef, compiledBody.profileRef],
      [profileBody.applicationOntologyPackageRef, compiledBody.applicationOntologyPackageRef],
      [profileBody.acceptanceManifestRef, compiledBody.manifestRef],
      [profileBody.hostCapabilityManifestRef, compiledBody.hostCapabilityManifestRef],
      [profileBody.publicationPolicyRef, compiledBody.publicationPolicyRef],
      [requiredCapabilitySliceRef, compiledBody.requiredCapabilitySliceRef],
      [requiredApplicationTopologySliceRef, compiledBody.requiredApplicationTopologySliceRef],
      [input.requiredApplicationTopologySlice.applicationOntologyPackageRef,
        compiledBody.applicationOntologyPackageRef],
      [input.requiredApplicationTopologySlice.manifestRef, compiledBody.manifestRef],
      [input.compilerIdentityRef, compiledBody.compilerIdentityRef],
      [input.profileSelectionRef, input.attempt.profileSelectionRef],
      [input.projectScopeRef, input.attempt.projectScopeRef],
      [input.actorScopeRef, input.attempt.actorScopeRef],
    ];
    if (internalPairs.some(([left, right]) => !sameRef(left, right)) ||
      input.requiredCapabilitySlice.manifestHash !== compiledBody.manifestRef.contentHash ||
      !sameRefSet(profileBody.datasourceContractRefs, compiledBody.datasourceContractRefs) ||
      !sameRefSet(profileBody.authContractRefs, compiledBody.authContractRefs) ||
      input.sealedInputRef.contentHash !== input.attempt.sealedInputHash ||
      input.evidenceRealmRef.contentHash !== input.attempt.evidenceRealmHash ||
      !validKgPreflight(input.attempt.kgPreflight)) {
      throw new Error("identity");
    }

    return assertApplicationOntologyGenerationBinding({
      schemaVersion: "cogo.application-ontology-generation-binding.v1",
      profileRef: cloneRef(profileRef),
      applicationOntologyPackageRef: cloneRef(compiledBody.applicationOntologyPackageRef),
      profileSelectionRef: cloneRef(input.profileSelectionRef),
      projectScopeRef: cloneRef(input.projectScopeRef),
      actorScopeRef: cloneRef(input.actorScopeRef),
      manifestRef: cloneRef(compiledBody.manifestRef),
      compiledOntologyRef: cloneRef(compiledOntologyRef),
      requiredCapabilitySliceRef: cloneRef(requiredCapabilitySliceRef),
      requiredApplicationTopologySliceRef: cloneRef(requiredApplicationTopologySliceRef),
      requiredCapabilityCatalogRef: cloneRef(compiledBody.requiredCapabilityCatalogRef),
      runtimeConsumerCatalogRef: cloneRef(compiledBody.runtimeConsumerCatalogRef),
      applicationGenerationEligibilityRef: cloneRef(input.applicationGenerationEligibilityRef),
      hostManifestRef: cloneRef(compiledBody.hostCapabilityManifestRef),
      validatorRef: cloneRef(input.validatorRef),
      compilerIdentityRef: cloneRef(compiledBody.compilerIdentityRef),
      promptTemplateRef: cloneRef(input.promptTemplateRef),
      sealedInputRef: cloneRef(input.sealedInputRef),
      kgPreflight: cloneKgPreflight(input.attempt.kgPreflight),
      modelPolicyRef: cloneRef(input.modelPolicyRef),
      evidenceRealmRef: cloneRef(input.evidenceRealmRef),
      receiptChainHeadRef: cloneRef(input.receiptChainHeadRef),
    });
  } catch {
    throw new Error("application-ontology-generation-binding-identity-mismatch");
  }
}
